import { Injectable } from "@angular/core";
import {
  AuthorizeNetGetCustomerProfileResponse,
  AuthorizeNetPaymentProfile,
} from "../../models/payment-gateway-responses.interface";
import {
  QPilotPaymentMethod,
  QPilotPaymentMethodType,
} from "../../models/qpilot/payment-method.interface";
import {
  normalizeCountryCode,
  normalizeExpiration,
} from "../../utils/country.utils";

/**
 * Authorize.Net helper service
 * Maps Authorize.Net API responses to QPilot payment method format
 */
@Injectable({
  providedIn: "root",
})
export class AuthorizeNetHelper {
  /**
   * Map Authorize.Net customer profile response to QPilot payment methods
   * Matches old QMC authorize-net.helper.ts mapProfiles method
   * @param response Authorize.Net customer profile response
   * @returns Array of QPilot payment methods
   */
  mapProfiles(
    response: AuthorizeNetGetCustomerProfileResponse,
  ): QPilotPaymentMethod[] {
    const paymentMethods: QPilotPaymentMethod[] = [];

    if (!response.profile || !response.profile.paymentProfiles) {
      return paymentMethods;
    }

    for (const profile of response.profile.paymentProfiles) {
      const paymentMethod = this.mapPaymentProfile(
        profile,
        response.profile.customerProfileId,
      );
      paymentMethods.push(paymentMethod);
    }

    return paymentMethods;
  }

  /**
   * Map a single Authorize.Net payment profile to QPilot payment method
   * @param profile Authorize.Net payment profile
   * @param customerProfileId Customer profile ID
   * @returns QPilot payment method
   */
  private mapPaymentProfile(
    profile: AuthorizeNetPaymentProfile,
    customerProfileId: string,
  ): QPilotPaymentMethod {
    const creditCard = profile.payment.creditCard;
    const billTo = profile.billTo;

    // Extract last 4 digits from masked card number (format: XXXX1234)
    let lastFourDigits = "";
    if (creditCard?.cardNumber) {
      const cardNumber = creditCard.cardNumber.replace(/X/g, "");
      lastFourDigits = cardNumber.slice(-4);
    }

    // Build description from card type and last 4
    let description = "";
    if (creditCard?.cardType && lastFourDigits) {
      description = `${creditCard.cardType} ending in ${lastFourDigits}`;
    } else if (lastFourDigits) {
      description = `Card ending in ${lastFourDigits}`;
    } else {
      description = "Payment Method";
    }

    return {
      Id: 0, // Will be set when saved to QPilot
      CustomerId: 0, // Will be set by caller
      Type: QPilotPaymentMethodType.AuthorizeNet,
      GatewayCustomerId: customerProfileId,
      GatewayPaymentId: profile.customerPaymentProfileId,
      Description: description,
      LastFourDigits: lastFourDigits,
      // Normalize expiration from "YYYY-MM" to "YYYYMM" format
      Expiration: normalizeExpiration(creditCard?.expirationDate),
      BillingFirstName: billTo?.firstName || undefined,
      BillingLastName: billTo?.lastName || undefined,
      BillingStreet1: billTo?.address || undefined,
      BillingStreet2: undefined,
      BillingCity: billTo?.city || undefined,
      BillingState: billTo?.state || undefined,
      BillingPostcode: billTo?.zip || undefined,
      // Normalize country from "USA" to "US" (2-char ISO 3166-1 alpha-2)
      BillingCountry: normalizeCountryCode(billTo?.country),
    };
  }
}
